import { useEffect, useLayoutEffect, useRef } from 'react';
import type { SidebarSide } from './useSidebarLayout';
import { canRestoreTo, otherModalOpen, toggleFor } from './sheetFocus';

interface SheetFocusRestore {
  side: SidebarSide;
  isCompact: boolean;
  /** The sheet is showing and its focus trap is on. */
  trapOpen: boolean;
  /** The other side's sheet is open: this one closed to make way, and focus belongs to that one. */
  otherSheetOpen: boolean;
  /** Focus went back to this opener outside the sheet; the layout watches that it stays somewhere usable. */
  onRestored: (side: SidebarSide, opener: HTMLElement) => void;
}

/**
 * When a sheet closes, focus goes back to whatever opened it, or to its toggle when that is gone, in a sheet or
 * inert. The trap's own restore cannot tell: it hands focus to whatever it saw last, often inside the sheet.
 * The opener is read in a layout effect, before the trap moves focus in; the restore runs as a passive effect,
 * once the closed trap has stopped pulling focus back.
 */
export function useSheetFocusRestore({ side, isCompact, trapOpen, otherSheetOpen, onRestored }: SheetFocusRestore): void {
  const wasOpen = useRef(trapOpen);
  const opener = useRef<Element | null>(null);
  useLayoutEffect(() => {
    if (trapOpen) opener.current = document.activeElement;
  }, [trapOpen]);
  useEffect(() => {
    const closed = wasOpen.current && !trapOpen;
    wasOpen.current = trapOpen;
    if (!closed) return;
    const from = opener.current;
    opener.current = null;
    // Leaving compact keeps the content on screen as a panel, and a modal or the other sheet owns focus.
    if (!isCompact || otherSheetOpen || otherModalOpen()) return;
    if (canRestoreTo(from)) {
      from.focus();
      onRestored(side, from);
    } else {
      toggleFor(side)?.focus();
    }
  }, [trapOpen, isCompact, otherSheetOpen, side, onRestored]);
}
